import { useCallback } from 'react';
import { ConnectionHandler, type RecordProxy, type RecordSourceProxy } from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useConnectionsListActions,
	type ConnectionDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';

const JIRA_SPREADSHEET_GROUP_TYPENAME = 'JiraSpreadsheetGroup';

export const getIssueIndexInConnection = ({
	issues,
	issuesWithContext,
	context,
}: {
	issues: RecordProxy<{}>[];
	issuesWithContext: Set<string>;
	context: {
		afterIssueId: string | null | undefined;
		beforeIssueId: string | null | undefined;
		position: number;
		sourceIssueId: string;
	};
}) => {
	let foundMovedIssue = false;

	// There is a possible race condition where the bulk query for positions fires without all the issue subscriptions arriving (when an issue is created right as the timeout for the client finishes)
	// In those cases, we should use relative positioning instead of the absolute position where possible to prevent issues from being inserted at the wrong position
	const relativeIssueIndex = issues.findIndex((issue) => {
		const issueId = issue.getLinkedRecord('node')?.getValue('issueId');
		if (issueId === context.sourceIssueId) {
			foundMovedIssue = true;
		}
		if (!issueId || (typeof issueId === 'string' && issuesWithContext.has(issueId))) {
			return false;
		}
		return issueId === context.afterIssueId || issueId === context.beforeIssueId;
	});

	let relativePosition: number | undefined;
	if (relativeIssueIndex !== -1) {
		const relativeIssue = issues[relativeIssueIndex];
		const relativeIssueId = relativeIssue?.getLinkedRecord('node')?.getValue('issueId');
		if (relativeIssueId === context.afterIssueId) {
			relativePosition = relativeIssueIndex;
		} else if (relativeIssueId === context.beforeIssueId) {
			relativePosition = relativeIssueIndex + 1;
		}
		if (foundMovedIssue && relativePosition != null) {
			// if we moved the issue down the list, we need to adjust the position otherwise its original position in the list impacts the index of all issues after
			relativePosition -= 1;
		}
	}

	return relativePosition ?? context.position;
};

const removeGroupFromConnection = ({
	store,
	groupId,
	connectionId,
	groupConnectionId,
}: {
	store: RecordSourceProxy;
	groupId: string;
	connectionId?: string;
	groupConnectionId?: string;
}) => {
	if (!groupConnectionId) {
		return;
	}

	if (connectionId) {
		// invalidate connection for next incoming updates.
		const connectionRecord = store.get(connectionId);
		connectionRecord?.invalidateRecord();
	}

	const relayId = RelayDataID({ id: groupId }, JIRA_SPREADSHEET_GROUP_TYPENAME);
	const groupConnectionRecord = store.get(groupConnectionId);

	if (!groupConnectionRecord || !relayId) {
		return;
	}

	ConnectionHandler.deleteNode(groupConnectionRecord, relayId);

	// Remove the firstGroup reference if it's the same as the group being removed
	if (groupConnectionRecord.getLinkedRecord('firstGroup')?.getDataID() === relayId) {
		groupConnectionRecord.setValue(null, 'firstGroup');
	}
};

export const updateGroupIssueCount = ({
	store,
	groupId,
	netChangeInIssueCount,
	groupConnectionId,
	connectionId,
}: {
	store: RecordSourceProxy;
	groupId: string;
	netChangeInIssueCount: number;
	groupConnectionId?: string;
	connectionId?: string;
}) => {
	const relayId = RelayDataID({ id: groupId }, JIRA_SPREADSHEET_GROUP_TYPENAME);

	if (!relayId) {
		return;
	}

	const groupRecord = store.get(relayId);
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const issueCount = (groupRecord?.getValue('issueCount') as number) || 0;
	const newIssueCount = issueCount + netChangeInIssueCount;

	if (newIssueCount <= 0) {
		removeGroupFromConnection({ store, groupId, connectionId, groupConnectionId });
	} else {
		groupRecord?.setValue(newIssueCount, 'issueCount');
	}
};

export const updateParentHasChildren = ({
	store,
	parentIssueEdgeRecordId,
	connectionId,
	hasChildren,
	projectKey,
}: {
	store: RecordSourceProxy;
	parentIssueEdgeRecordId: string;
	connectionId: string | undefined;
	hasChildren: boolean;
	projectKey?: string;
}): boolean => {
	// returns whether an update was performed
	const parentIssueRecord = store.get(parentIssueEdgeRecordId);
	const updateArgs = projectKey ? { filterByProjectKeys: [projectKey] } : undefined;
	if (parentIssueRecord && parentIssueRecord.getValue('hasChildren', updateArgs) !== hasChildren) {
		parentIssueRecord?.setValue(hasChildren, 'hasChildren', updateArgs);
		if (hasChildren === false && connectionId && fg('jsc_list_reparenting')) {
			const connectionRecord = store.get(connectionId);
			connectionRecord?.invalidateRecord();
		}
		return true;
	}
	return false;
};

export const useUpdateConnectionDetails = () => {
	const { removeConnection: removeConnectionFromList } = useConnectionsListActions();

	return useCallback(
		({
			store,
			connection,
			netChangeInIssueCount,
			remainingIssueCount,
			groupConnectionId,
			projectKey,
		}: {
			store: RecordSourceProxy;
			connection: ConnectionDetails;
			netChangeInIssueCount: number;
			remainingIssueCount?: number;
			groupConnectionId?: string;
			projectKey?: string;
		}) => {
			if (remainingIssueCount === undefined) {
				return;
			}

			switch (connection.type) {
				case 'GROUP_CHILDREN':
					updateGroupIssueCount({
						store,
						groupId: connection.groupId,
						netChangeInIssueCount,
						groupConnectionId,
						connectionId: connection.connectionId,
					});
					break;
				case 'PARENT_CHILDREN':
					updateParentHasChildren({
						store,
						parentIssueEdgeRecordId: connection.parentRelayId,
						connectionId: connection.connectionId,
						hasChildren: remainingIssueCount > 0,
						projectKey,
					});
					break;
				case 'ROOT_ISSUES': {
					const connectionRecord = store.get(connection.connectionId);
					if (!connectionRecord) {
						break;
					}
					const totalIssues = connectionRecord.getValue('totalIssueSearchResultCount');
					if (totalIssues != null && typeof totalIssues === 'number') {
						connectionRecord.setValue(
							totalIssues + netChangeInIssueCount,
							'totalIssueSearchResultCount',
						);
					}
					break;
				}
				default:
			}

			if (remainingIssueCount === 0) {
				removeConnectionFromList(connection);
			}
		},
		[removeConnectionFromList],
	);
};

export type UseUpdateConnectionDetailsReturnType = ReturnType<typeof useUpdateConnectionDetails>;

function insertEdgeAtPosition<T = {}>(
	existingEdges: RecordProxy<T>[],
	newEdge: RecordProxy<T>,
	index: number,
): RecordProxy<T>[] {
	return [...existingEdges.slice(0, index), newEdge, ...existingEdges.slice(index)];
}

export const insertNewGroupInGroupsConnection = ({
	store,
	groupsConnection,
	newGroup,
	groupEdges,
	afterGroupId,
}: {
	store: RecordSourceProxy;
	groupsConnection: RecordProxy<{}>;
	newGroup: RecordProxy<{}>;
	groupEdges: RecordProxy<{}>[];
	afterGroupId?: string | null;
}) => {
	const newGroupEdge = ConnectionHandler.createEdge(
		store,
		groupsConnection,
		newGroup,
		'JiraSpreadsheetGroupEdge',
	);

	if (afterGroupId === null) {
		return insertEdgeAtPosition(groupEdges, newGroupEdge, 0);
	}

	const afterGroupIndex = groupEdges.findIndex(
		(edge) => edge.getLinkedRecord('node')?.getValue('id') === afterGroupId,
	);

	if (afterGroupIndex !== -1) {
		return insertEdgeAtPosition(groupEdges, newGroupEdge, afterGroupIndex + 1);
	}

	return groupEdges;
};
